import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import "./Login.css";

const ACCOUNT_REQUEST_ERROR_MESSAGES = {
	missing_token: "This link is invalid or incomplete.",
	invalid_or_used:
		"This approval link has already been used or is no longer valid.",
	server: "Something went wrong. Please try again later.",
	create_failed:
		"The account could not be created. The username may already be in use.",
	username_taken: "That username is no longer available.",
};

function Login({ onLogin }) {
	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");
	const [email, setEmail] = useState("");
	const [isRequest, setIsRequest] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	// Account request redirect feedback (approve/deny links land here)
	const queryString = searchParams.toString();
	useEffect(() => {
		const approved = searchParams.get("accountApproved");
		const denied = searchParams.get("accountDenied");
		const errKey = searchParams.get("accountError");
		if (approved === "1") {
			setSuccess(
				"Your account was approved. Check your email for a temporary password, then sign in below.",
			);
			setSearchParams({}, { replace: true });
		} else if (denied === "1") {
			setError("Your account request was not approved.");
			setSearchParams({}, { replace: true });
		} else if (errKey) {
			setError(
				ACCOUNT_REQUEST_ERROR_MESSAGES[errKey] ||
					"Something went wrong with that link.",
			);
			setSearchParams({}, { replace: true });
		}
	}, [queryString, setSearchParams]);

	// Check for stored errors on mount (only from sessionStorage - current session errors)
	useEffect(() => {
		// Check sessionStorage for auth errors (from auth interceptor - these are current session errors)
		const sessionError = sessionStorage.getItem("auth_error");
		if (sessionError) {
			try {
				const errorData = JSON.parse(sessionError);
				setError(
					errorData.message ||
						"Session expired. Please log in again.",
				);
				sessionStorage.removeItem("auth_error");
			} catch (e) {
				// Ignore parse errors
			}
		}

		// Clear any old localStorage errors on page load - only show errors from actual login attempts
		localStorage.removeItem("last_login_error");
	}, []);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		setSuccess("");
		setLoading(true);

		try {
			if (isRequest) {
				// Request account
				const { data } = await axios.post("/api/auth/request-account", {
					username,
					email,
				});
				setSuccess(
					data?.message ||
						"Request submitted. An administrator will review it.",
				);
				setUsername("");
				setEmail("");
				setIsRequest(false);
			} else {
				// Login
				try {
					const response = await axios.post("/api/auth/login", {
						username,
						password,
					});

					const permissions = response.data.permissions || [];

					// Check if password change is required
					if (response.data.mustChangePassword) {
						localStorage.setItem("token", response.data.token);
						localStorage.setItem(
							"user",
							JSON.stringify(response.data.user),
						);
						localStorage.setItem(
							"permissions",
							JSON.stringify(permissions),
						);
						localStorage.setItem("mustChangePassword", "true");

						if (onLogin) {
							onLogin(
								response.data.user,
								response.data.token,
								true,
								permissions,
							);
						}
						// Don't navigate - will be handled by App.js
						return;
					}

					localStorage.setItem("token", response.data.token);
					localStorage.setItem(
						"user",
						JSON.stringify(response.data.user),
					);
					localStorage.setItem(
						"permissions",
						JSON.stringify(permissions),
					);
					localStorage.removeItem("mustChangePassword");

					if (onLogin) {
						onLogin(
							response.data.user,
							response.data.token,
							false,
							permissions,
						);
					}
					const nextRaw = searchParams.get("next");
					const nextPath =
						nextRaw &&
						nextRaw.startsWith("/") &&
						!nextRaw.startsWith("//") &&
						!nextRaw.includes("://")
							? nextRaw
							: "/";
					navigate(nextPath);
				} catch (loginError) {
					console.error(
						"Login error:",
						loginError.response?.status,
						loginError.message,
					);

					if (loginError.response?.data?.error) {
						setError(loginError.response.data.error);
					} else if (loginError.response?.status === 401) {
						setError(
							"Invalid username or password. Please check your credentials and try again.",
						);
					} else if (
						loginError.message &&
						loginError.message.includes("Network")
					) {
						setError(
							"Unable to connect to server. Please check your connection and try again.",
						);
					} else {
						setError("Login failed. Please try again.");
					}
				}
			}
		} catch (error) {
			// This catch block is for account request errors
			setError(error.response?.data?.error || "An error occurred");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="login-container">
			<div className="login-stage">
				<div className="login-card">
					<h2>{isRequest ? "Request an account" : "Welcome back"}</h2>
					<form onSubmit={handleSubmit}>
						<div className="form-group">
							<label htmlFor="login-username">Username</label>
							<input
								id="login-username"
								type="text"
								value={username}
								onChange={(e) => setUsername(e.target.value)}
								required
								disabled={loading}
								autoComplete="username"
								autoFocus
							/>
						</div>
						{isRequest && (
							<div className="form-group">
								<label htmlFor="login-email">
									Email{" "}
									<span className="required-mark">*</span>
								</label>
								<input
									id="login-email"
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									disabled={loading}
									placeholder="your@email.com"
									required
									autoComplete="email"
								/>
							</div>
						)}
						{!isRequest && (
							<div className="form-group">
								<label htmlFor="login-password">Password</label>
								<input
									id="login-password"
									type="password"
									value={password}
									onChange={(e) =>
										setPassword(e.target.value)
									}
									required
									disabled={loading}
									autoComplete="current-password"
								/>
							</div>
						)}
						{error && (
							<div className="login-error" role="alert">
								<div className="login-error-message">
									{error}
								</div>
							</div>
						)}
						{success && (
							<div className="success-message" role="status">
								{success}
							</div>
						)}
						<button
							type="submit"
							className="btn btn-primary login-submit"
							disabled={loading}
						>
							{loading
								? "Please wait..."
								: isRequest
									? "Request Account"
									: "Sign in"}
						</button>
					</form>
					<div className="login-footer">
						<button
							type="button"
							className="btn-link"
							onClick={() => {
								setIsRequest(!isRequest);
								setError("");
								setSuccess("");
							}}
						>
							{isRequest
								? "Already have an account? Sign in"
								: "Need an account? Request one"}
						</button>
						{!isRequest && (
							<button
								type="button"
								className="btn-link"
								onClick={() => navigate("/forgot-password")}
							>
								Forgot password?
							</button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

export default Login;
